import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { TrendingUp, Users, Leaf, DollarSign } from 'lucide-react';
import { calculateCO2Savings } from '../constants/gamification';

interface CommunityStats {
  totalCommunityItemsSaved: number;
  totalCommunityCO2Saved: number;
  totalCommunityMoneySaved: number;
  lastUpdated: any;
}

export function CommunityStatsWidget() {
  const [stats, setStats] = useState<CommunityStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen to global community stats in real-time
    const globalStatsRef = doc(db, 'globalStats', 'community');

    const unsubscribe = onSnapshot(
      globalStatsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setStats(snapshot.data() as CommunityStats);
        } else {
          // Initialize if doesn't exist
          setStats({
            totalCommunityItemsSaved: 0,
            totalCommunityCO2Saved: 0,
            totalCommunityMoneySaved: 0,
            lastUpdated: new Date(),
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading community stats:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-primary-green to-emerald-600 rounded-2xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-6 h-6" />
          <h3 className="text-lg font-bold">Community Impact</h3>
        </div>
        <p className="text-sm opacity-90">Loading stats...</p>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="bg-gradient-to-br from-primary-green to-emerald-600 rounded-2xl shadow-lg p-6 text-white overflow-hidden relative">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 opacity-10">
        <Leaf className="w-32 h-32 transform rotate-12" />
      </div>

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Users className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Community Impact</h3>
            <p className="text-sm opacity-90">Together we're making a difference!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Items Saved */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium opacity-90">Items Saved</span>
            </div>
            <p className="text-3xl font-bold">
              {stats.totalCommunityItemsSaved.toLocaleString()}
            </p>
            <p className="text-xs opacity-75 mt-1">Food items rescued</p>
          </div>

          {/* CO2 Saved */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Leaf className="w-5 h-5" />
              <span className="text-sm font-medium opacity-90">CO₂ Saved</span>
            </div>
            <p className="text-3xl font-bold">
              {stats.totalCommunityCO2Saved.toLocaleString()} kg
            </p>
            <p className="text-xs opacity-75 mt-1">Carbon footprint reduced</p>
          </div>

          {/* Money Saved */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm font-medium opacity-90">Money Saved</span>
            </div>
            <p className="text-3xl font-bold">
              ${Math.round(stats.totalCommunityMoneySaved).toLocaleString()}
            </p>
            <p className="text-xs opacity-75 mt-1">Collective savings</p>
          </div>
        </div>

        <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-xl p-4">
          <p className="text-sm text-center">
            <span className="font-semibold">🌍 You're part of something bigger!</span>
            <br />
            <span className="opacity-90">
              Join {Math.ceil(stats.totalCommunityItemsSaved / 20).toLocaleString()}+ households fighting food waste
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
